import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useToast } from './components/ui/Toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { CartDrawer } from './components/cart/CartDrawer';
import Spinner from './components/ui/Spinner';

// DevPanel chargé uniquement en dev
const DevPanel = import.meta.env.DEV
  ? lazy(() => import('./components/dev/DevPanel'))
  : () => null;

// Pages client - Lazy loaded
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const Category = lazy(() => import('./pages/Category'));
const Product = lazy(() => import('./pages/Product'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Account = lazy(() => import('./pages/Account'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const EmailConfirmed = lazy(() => import('./pages/EmailConfirmed'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Pages admin - Lazy loaded (chunk séparé)
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const AdminCustomers = lazy(() => import('./pages/admin/Customers'));
const AdminCustomerDetail = lazy(() => import('./pages/admin/CustomerDetail'));

// Composant de chargement pour les pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Spinner size="lg" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
}

function AuthHashHandler() {
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash === '#') return;

    const params = new URLSearchParams(hash.substring(1));

    // Gestion des erreurs auth (lien expiré, etc.)
    if (params.get('error')) {
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');

      if (errorCode === 'otp_expired') {
        toast.error('Le lien a expiré. Veuillez en demander un nouveau.');
      } else if (errorDescription) {
        toast.error(decodeURIComponent(errorDescription.replace(/\+/g, ' ')));
      }
      window.location.hash = '';
      navigate('/connexion', { replace: true });
      return;
    }

    // Redirection après confirmation email
    if (params.get('type') === 'signup' && params.get('access_token')) {
      navigate('/email-confirme', { replace: true });
    }
  }, [navigate, toast]);

  return null;
}

function SiteLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 px-4 py-10 sm:px-6">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CartProvider>
            <ToastProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AuthHashHandler />
                {import.meta.env.DEV && (
                  <Suspense fallback={null}>
                    <DevPanel />
                  </Suspense>
                )}
                <Routes>
                  <Route element={<SiteLayout />}>
                    <Route index element={<Home />} />
                    {/* Routes en français */}
                    <Route path="/boutique" element={<Shop />} />
                    <Route path="/categorie/:slug" element={<Category />} />
                    <Route path="/produit/:slug" element={<Product />} />
                    <Route path="/panier" element={<Cart />} />
                    <Route path="/commander" element={<Checkout />} />
                    <Route path="/compte" element={<Account />} />
                    <Route path="/connexion" element={<Login />} />
                    <Route path="/inscription" element={<Register />} />
                    <Route path="/mot-de-passe-oublie" element={<ResetPassword />} />
                    <Route path="/email-confirme" element={<EmailConfirmed />} />
                    <Route path="/a-propos" element={<About />} />
                    {/* Routes en anglais (alias) */}
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/category/:slug" element={<Category />} />
                    <Route path="/product/:slug" element={<Product />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/email-confirmed" element={<EmailConfirmed />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                  </Route>

                  <Route
                    path="/admin"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AdminLayout />
                      </Suspense>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="produits" element={<AdminProducts />} />
                    <Route path="produits/nouveau" element={<AdminProductEdit />} />
                    <Route path="produits/:slug/edit" element={<AdminProductEdit />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="commandes" element={<AdminOrders />} />
                    <Route path="commandes/:orderNumber" element={<AdminOrderDetail />} />
                    <Route path="clients" element={<AdminCustomers />} />
                    <Route path="clients/:customerId" element={<AdminCustomerDetail />} />
                    {/* Routes en anglais (alias) */}
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="products/new" element={<AdminProductEdit />} />
                    <Route path="products/:slug/edit" element={<AdminProductEdit />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="orders/:orderNumber" element={<AdminOrderDetail />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="customers/:customerId" element={<AdminCustomerDetail />} />
                  </Route>

                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </ToastProvider>
          </CartProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
